import { useEffect, useRef, useState } from 'preact/hooks';
import { detectTimezone } from 'shared/dateutil';
import { BtnPrimary } from '@components/buttons';
import { IcoChat, IcoClipboard, IcoRefresh, IcoUsers } from '@components/icons';
import { useIntl } from 'shared/intl/use-intl';
import { rpx } from 'client/lib/rpx-client';
import { PriceRow } from 'server/types';
import { ComponentType } from 'preact';
import { showAipUpgradeModal } from './aip-upgrade-modal';
import { AipFoundingRatePrice } from './aip-founding-rate-price';
import { AipTrialClaimSummary } from './aip-trial-claim-summary';
import { formatAccessDate } from './aip-access-date';

type GatedFeature = 'discussions' | 'meetings' | 'assignments';

type SvgProps = { class?: string };

type FeatureCopy = {
  heading: string;
  description: string;
  Icon: ComponentType<SvgProps>;
};

const FEATURE_COPY: Record<GatedFeature, FeatureCopy> = {
  discussions: {
    heading: 'Join the discussion',
    description:
      'Discussions are where AI Playground members share their stories, ask questions, and get feedback from the community.',
    Icon: IcoChat,
  },
  meetings: {
    heading: 'Join the live calls',
    description: 'AI Playground members meet live each week for group coaching, practice, and Q&A.',
    Icon: IcoUsers,
  },
  assignments: {
    heading: 'Submit for feedback',
    description:
      'AI Playground members submit assignments and receive feedback from guides and the community.',
    Icon: IcoClipboard,
  },
};

export function AipGatedOverlay({ feature }: { feature: GatedFeature }) {
  const intl = useIntl();
  const attemptedRef = useRef(false);
  const [state, setState] = useState<
    | {
        price: PriceRow;
        freeTrialDays: number;
        firstChargeAt?: string;
        pendingAccessDate?: string;
      }
    | undefined
  >(undefined);

  const { heading, description, Icon } = FEATURE_COPY[feature];

  useEffect(() => {
    if (attemptedRef.current) {
      return;
    }
    attemptedRef.current = true;
    rpx.aipUpgrade.provisionAipUpgrade().then(
      () => location.reload(),
      () => {},
    );
    rpx.aipUpgrade.getAipUpgradeState({ timezone: detectTimezone() }).then(
      (data) =>
        setState({
          price: data.price as PriceRow,
          freeTrialDays: data.freeTrialDays,
          firstChargeAt: data.firstChargeAt,
          pendingAccessDate: data.pendingAccessDate,
        }),
      () => {},
    );
  }, []);

  const hasTrial = !!state && state.freeTrialDays > 0;
  // Already bought, waiting on the cohort to open. Selling it again here would
  // charge them the standard price on top of the months they are waiting for.
  const pendingAccessDate = state?.pendingAccessDate;

  async function handleUpgrade() {
    const success = await showAipUpgradeModal();
    if (success) {
      location.reload();
    }
  }

  return (
    <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-6 py-8 text-center flex flex-col items-center gap-3">
      <Icon class="size-8 text-theme-link dark:text-gray-300" />
      <h3 class="text-lg font-semibold dark:text-white">{intl(heading)}</h3>
      <p class="text-gray-600 dark:text-gray-400 text-sm max-w-md">{intl(description)}</p>
      {hasTrial && state && !pendingAccessDate && (
        <div class="w-full max-w-md">
          <AipTrialClaimSummary
            price={state.price}
            freeTrialDays={state.freeTrialDays}
            firstChargeAt={state.firstChargeAt}
          />
        </div>
      )}
      {pendingAccessDate ? (
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Your access opens {formatAccessDate(pendingAccessDate)}.
        </p>
      ) : (
        <>
          <BtnPrimary class="mt-2" onClick={handleUpgrade}>
            {hasTrial ? intl('Start my free months') : intl('Join AI Playground')}
          </BtnPrimary>
          {state && !hasTrial && (
            <div class="text-sm text-gray-600 dark:text-gray-400">
              <AipFoundingRatePrice price={state.price} />
            </div>
          )}
        </>
      )}
      <button
        type="button"
        onClick={() => location.reload()}
        class="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mt-1 inline-flex items-center gap-1"
      >
        <IcoRefresh class="size-3" />
        {intl('Already a member? Refresh')}
      </button>
    </div>
  );
}
